import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect, lazy, Suspense } from "react";
import PWAInstallPrompt from "@/components/app/PWAInstallPrompt";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/app/ProtectedRoute";
import ErrorBoundary from "@/components/app/ErrorBoundary";
import Layout from "@/components/app/Layout";

// Eager: giriş + açılış sayfası (ilk boya anında gelsin)
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";

// Lazy: diğer tüm sayfalar ayrı chunk'lara bölünür → ilk yük ve geçiş çok daha hızlı.
const PublicMenu = lazy(() => import("@/pages/PublicMenu"));
const Branches = lazy(() => import("@/pages/Branches"));
const Users = lazy(() => import("@/pages/Users"));
const UserPermissionsMatrix = lazy(() => import("@/pages/UserPermissionsMatrix"));
const Personnel = lazy(() => import("@/pages/Personnel"));
const ExpenseCategories = lazy(() => import("@/pages/ExpenseCategories"));
const ExpenseAnalytics = lazy(() => import("@/pages/ExpenseAnalytics"));
const ProductTypes = lazy(() => import("@/pages/ProductTypes"));
const CentralExpenses = lazy(() => import("@/pages/CentralExpenses"));
const ConsumptionReport = lazy(() => import("@/pages/ConsumptionReport"));
const ReportForm = lazy(() => import("@/pages/ReportForm"));
const ReportsList = lazy(() => import("@/pages/ReportsList"));
const Balance = lazy(() => import("@/pages/Balance"));
const Timesheet = lazy(() => import("@/pages/Timesheet"));
const EnvelopePrint = lazy(() => import("@/pages/EnvelopePrint"));
const Import = lazy(() => import("@/pages/Import"));
const Help = lazy(() => import("@/pages/Help"));
const StockCatalog = lazy(() => import("@/pages/StockCatalog"));
const StockCount = lazy(() => import("@/pages/StockCount"));
const BranchPerformance = lazy(() => import("@/pages/BranchPerformance"));
const AiAssistant = lazy(() => import("@/pages/AiAssistant"));
const DailySummary = lazy(() => import("@/pages/DailySummary"));
const AdvanceRequests = lazy(() => import("@/pages/AdvanceRequests"));
const PersonnelRequests = lazy(() => import("@/pages/PersonnelRequests"));
const PlatformAnalytics = lazy(() => import("@/pages/PlatformAnalytics"));
const PlatformOverview = lazy(() => import("@/pages/PlatformAnalytics").then((m) => ({ default: m.PlatformOverview })));
const PlatformDetail = lazy(() => import("@/pages/PlatformAnalytics").then((m) => ({ default: m.PlatformDetail })));
const PlatformUploads = lazy(() => import("@/pages/PlatformUploads"));
const PlatformMatching = lazy(() => import("@/pages/PlatformMatching"));
const HeadOfficePersonnel = lazy(() => import("@/pages/HeadOfficePersonnel"));
const HRSettings = lazy(() => import("@/pages/HRSettings"));
const MerkezDaily = lazy(() => import("@/pages/MerkezDaily"));
const MerkezAccountPayments = lazy(() => import("@/pages/MerkezAccountPayments"));
const NotificationSettings = lazy(() => import("@/pages/NotificationSettings"));
const DailyEmailSettings = lazy(() => import("@/pages/DailyEmailSettings"));
const Announcements = lazy(() => import("@/pages/Announcements"));
const MenuBoard = lazy(() => import("@/pages/MenuBoard"));
const JokerTakip = lazy(() => import("@/pages/JokerTakip"));
const ScreenPlayer = lazy(() => import("@/pages/ScreenPlayer"));
const ScreenShortRedirect = lazy(() => import("@/pages/ScreenShortRedirect"));
const StorageUsage = lazy(() => import("@/pages/StorageUsage"));
const TrendyolSettings = lazy(() => import("@/pages/TrendyolSettings"));
const TrendyolStoreMap = lazy(() => import("@/pages/TrendyolStoreMap"));
const TrendyolLive = lazy(() => import("@/pages/TrendyolLive"));
const TrendyolReconcile = lazy(() => import("@/pages/TrendyolReconcile"));
const TrendyolExternalBranches = lazy(() => import("@/pages/TrendyolExternalBranches"));
const TrendyolAnalytics = lazy(() => import("@/pages/TrendyolAnalytics"));
const TrendyolProducts = lazy(() => import("@/pages/TrendyolProducts"));
const TrendyolMutabakat = lazy(() => import("@/pages/TrendyolMutabakat"));
const TrendyolAnomalies = lazy(() => import("@/pages/TrendyolAnomalies"));
const TrendyolClaims = lazy(() => import("@/pages/TrendyolClaims"));
const WhatsApp = lazy(() => import("@/pages/WhatsApp"));
const UtilityBills = lazy(() => import("@/pages/UtilityBills"));
const MigrosMutabakat = lazy(() => import("@/pages/MigrosMutabakat"));
const MerkezExpenseCategories = lazy(() => import("@/pages/MerkezExpenseCategories"));
const MerkezReport = lazy(() => import("@/pages/MerkezReport"));
const MealCardPayments = lazy(() => import("@/pages/MealCardPayments"));
const OnlinePanel = lazy(() => import("@/pages/OnlinePanel"));
const MyOnlinePanel = lazy(() => import("@/pages/MyOnlinePanel"));
const RentTracking = lazy(() => import("@/pages/RentTracking"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const OnlinePanelMenus = lazy(() => import("@/pages/OnlinePanelMenus"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
const YsReconcile = lazy(() => import("@/pages/YsReconcile"));
const SupportSettings = lazy(() => import("@/pages/SupportSettings"));
const Franchise = lazy(() => import("@/pages/Franchise"));
const UnlavOrders = lazy(() => import("@/pages/UnlavOrders"));
const MuhasebeCari = lazy(() => import("@/pages/MuhasebeCari"));

// Public (Layout dışı) lazy sayfalar için hafif tam-ekran fallback
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
      <div className="h-8 w-8 rounded-full border-2 border-[#D95D39] border-t-transparent animate-spin" />
    </div>
  );
}

function App() {
  // Global: type="number" inputlarında mouse wheel ve ↑↓ ok tuşları sayfa kaydırmasını
  // engelliyordu (input değerini artırıp azaltıyorlardı). Bunları global düzeyde iptal et.
  useEffect(() => {
    const preventNumberWheel = (e) => {
      const el = e.target;
      if (el && el.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    const preventNumberArrows = (e) => {
      const el = e.target;
      if (
        el && el.tagName === "INPUT" && el.type === "number" &&
        (e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        e.preventDefault();
      }
    };
    document.addEventListener("wheel", preventNumberWheel, { passive: true });
    document.addEventListener("keydown", preventNumberArrows);
    return () => {
      document.removeEventListener("wheel", preventNumberWheel);
      document.removeEventListener("keydown", preventNumberArrows);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <PWAInstallPrompt />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/menu/:brand" element={<PublicMenu />} />
          <Route path="/help" element={<Help />} />
          <Route path="/screen/:screenId" element={<ScreenPlayer />} />
          <Route path="/s/:shortCode" element={<ScreenShortRedirect />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route
              path="branches"
              element={
                <ProtectedRoute requireAdmin>
                  <Branches />
                </ProtectedRoute>
              }
            />
            <Route
              path="users"
              element={
                <ProtectedRoute requireAdmin>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="user-permissions"
              element={
                <ProtectedRoute requireAdmin>
                  <UserPermissionsMatrix />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit-log"
              element={
                <ProtectedRoute requireAdmin>
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route path="personnel" element={<Personnel />} />
            <Route
              path="import"
              element={
                <ProtectedRoute requireAdmin>
                  <Import />
                </ProtectedRoute>
              }
            />
            <Route
              path="expense-categories"
              element={
                <ProtectedRoute requireAdmin>
                  <ExpenseCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="expense-analytics"
              element={
                <ProtectedRoute requireAdminOrAccounting>
                  <ExpenseAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="product-types"
              element={
                <ProtectedRoute requireAdmin>
                  <ProductTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="central-expenses"
              element={
                <ProtectedRoute requireAdminOrAccounting>
                  <CentralExpenses />
                </ProtectedRoute>
              }
            />
            <Route path="consumption" element={<ConsumptionReport />} />
            <Route
              path="branch-performance"
              element={
                <ProtectedRoute requireAdminOrRegional>
                  <BranchPerformance />
                </ProtectedRoute>
              }
            />
            <Route path="report/new" element={<ReportForm />} />
            <Route path="reports" element={<ReportsList />} />
            <Route path="daily-summary" element={<DailySummary />} />
            <Route path="balance" element={<Balance />} />
            <Route path="ai-assistant" element={
              <ProtectedRoute requireAdminOrRegional>
                <AiAssistant />
              </ProtectedRoute>
            } />
            <Route path="timesheet" element={<Timesheet />} />
            <Route path="zarf-yazdir" element={<EnvelopePrint />} />
            <Route path="advance-requests" element={<AdvanceRequests />} />
            <Route path="personnel-requests" element={<PersonnelRequests />} />
            <Route path="platform" element={<PlatformAnalytics />}>
              <Route index element={<PlatformOverview />} />
              <Route path="detail/:platform" element={<PlatformDetail />} />
            </Route>
            <Route path="platform/uploads" element={<PlatformUploads />} />
            <Route path="platform/matching" element={<PlatformMatching />} />
            <Route path="head-office" element={
              <ProtectedRoute requireAdminOrAccounting>
                <HeadOfficePersonnel />
              </ProtectedRoute>
            } />
            <Route path="hr-settings" element={
              <ProtectedRoute requireAdminOrAccounting>
                <HRSettings />
              </ProtectedRoute>
            } />
            <Route path="merkez-daily" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MerkezDaily />
              </ProtectedRoute>
            } />
            <Route path="merkez-account-payments" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MerkezAccountPayments />
              </ProtectedRoute>
            } />
            <Route path="notification-settings" element={
              <ProtectedRoute requireAdmin>
                <NotificationSettings />
              </ProtectedRoute>
            } />
            <Route path="daily-email" element={
              <ProtectedRoute requireAdmin>
                <DailyEmailSettings />
              </ProtectedRoute>
            } />
            <Route path="announcements" element={
              <ProtectedRoute requireAdmin>
                <Announcements />
              </ProtectedRoute>
            } />
            <Route path="menuboard" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MenuBoard />
              </ProtectedRoute>
            } />
            <Route path="joker-takip" element={
              <ProtectedRoute requireAdmin>
                <JokerTakip />
              </ProtectedRoute>
            } />
            <Route path="franchise" element={
              <ProtectedRoute requireAdmin>
                <Franchise />
              </ProtectedRoute>
            } />
            <Route path="unlav-orders" element={
              <ProtectedRoute>
                <UnlavOrders />
              </ProtectedRoute>
            } />
            <Route path="muhasebe-cari" element={
              <ProtectedRoute requireAdmin>
                <MuhasebeCari />
              </ProtectedRoute>
            } />
            <Route path="storage-usage" element={
              <ProtectedRoute requireAdmin>
                <StorageUsage />
              </ProtectedRoute>
            } />
            <Route path="trendyol" element={
              <ProtectedRoute requireAdmin>
                <TrendyolSettings />
              </ProtectedRoute>
            } />
            <Route path="trendyol/store-map" element={
              <ProtectedRoute requireAdmin>
                <TrendyolStoreMap />
              </ProtectedRoute>
            } />
            <Route path="trendyol/live" element={
              <ProtectedRoute>
                <TrendyolLive />
              </ProtectedRoute>
            } />
            <Route path="trendyol/reconcile" element={
              <ProtectedRoute requireAdmin>
                <TrendyolReconcile />
              </ProtectedRoute>
            } />
            <Route path="trendyol/analytics" element={
              <ProtectedRoute>
                <TrendyolAnalytics />
              </ProtectedRoute>
            } />
            <Route path="trendyol/products" element={
              <ProtectedRoute>
                <TrendyolProducts />
              </ProtectedRoute>
            } />
            <Route path="trendyol/mutabakat" element={
              <ProtectedRoute requireAdmin>
                <TrendyolMutabakat />
              </ProtectedRoute>
            } />
            <Route path="trendyol/anomalies" element={
              <ProtectedRoute>
                <TrendyolAnomalies />
              </ProtectedRoute>
            } />
            <Route path="trendyol/claims" element={
              <ProtectedRoute requireAdmin>
                <TrendyolClaims />
              </ProtectedRoute>
            } />
            <Route path="whatsapp" element={
              <ProtectedRoute>
                <WhatsApp />
              </ProtectedRoute>
            } />
            <Route path="utility-bills" element={
              <ProtectedRoute>
                <UtilityBills />
              </ProtectedRoute>
            } />
            <Route path="migros/mutabakat" element={
              <ProtectedRoute requireAdmin>
                <MigrosMutabakat />
              </ProtectedRoute>
            } />
            <Route path="trendyol/external-branches" element={
              <ProtectedRoute requireAdmin>
                <TrendyolExternalBranches />
              </ProtectedRoute>
            } />
            <Route path="merkez-expense-categories" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MerkezExpenseCategories />
              </ProtectedRoute>
            } />
            <Route path="merkez-report" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MerkezReport />
              </ProtectedRoute>
            } />
            <Route path="meal-card-payments" element={
              <ProtectedRoute requireAdminOrAccounting>
                <MealCardPayments />
              </ProtectedRoute>
            } />
            <Route path="rent-tracking" element={
              <ProtectedRoute requireAdminOrAccounting>
                <RentTracking />
              </ProtectedRoute>
            } />
            <Route path="online-panel" element={
              <ProtectedRoute requireAdminOrAccounting>
                <OnlinePanel />
              </ProtectedRoute>
            } />
            <Route path="my-online-panel" element={<MyOnlinePanel />} />
            <Route path="online-menus" element={
              <ProtectedRoute requireAdminOrAccounting>
                <OnlinePanelMenus />
              </ProtectedRoute>
            } />
            <Route path="recipes" element={
              <ProtectedRoute requireAdminOrAccounting>
                <Recipes />
              </ProtectedRoute>
            } />
            <Route path="support" element={<SupportTickets />} />
            <Route path="ys-reconcile" element={<YsReconcile />} />
            <Route path="support-settings" element={
              <ProtectedRoute requireAdmin>
                <SupportSettings />
              </ProtectedRoute>
            } />
            <Route path="stock-count" element={<StockCount />} />
            <Route
              path="stock-catalog"
              element={
                <ProtectedRoute requireAdmin>
                  <StockCatalog />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
